import { emptySplitApi as api } from "./baseApi";
import type { PEARReviewFormType } from '../components/forms/models/pearReviewModel';

export const addTagTypes = ["PEARReviewController"] as const;

// Types for PEAR Review API responses
export interface PEARReview extends PEARReviewFormType {
  id: string;
  employeeId: string;
  managerId?: string;
  managerName?: string;
  managerEditedAt?: string;
  status?: 'draft' | 'submitted' | 'under_review' | 'approved' | 'rejected';
  qualityOfWorkTotal?: number;
  workHabitsTotal?: number;
  jobKnowledgeTotal?: number;
  highPerformanceCultureTotal?: number;
  functionalSkillsTotal?: number;
  interpersonalSkillsTotal?: number;
  leadershipProblemSolvingTotal?: number;
  interpersonalTotal?: number;
  rawTotal?: number;
  overallScore?: number;
  finalGrade?: string;
  ratingDescription?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface PEARRating {
  grade: string;
  minPercentage: number;
  maxPercentage: number;
  description: string;
}

export interface PEARSummaryItem {
  employeeId: string;
  employeeName: string;
  designation: string;
  reviewPeriod: string;
  overallScore: number;
  finalGrade: string;
  ratingDescription: string;
}

const pearReviewApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Get all PEAR submissions for current user
      getMyPEARReviews: build.query<PEARReview[], void>({
        query: () => ({ url: `/pear-reviews/my-reviews` }),
        providesTags: ["PEARReviewController"],
      }),

      // Get all PEAR reviews (admin/manager)
      getAllPEARReviews: build.query<PEARReview[], { filter?: string } | void>({
        query: (queryArg) => ({
          url: `/pear-reviews`,
          params: queryArg ? { filter: queryArg.filter } : {},
        }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEAR review by ID
      getPEARById: build.query<PEARReview, { id: string }>({
        query: (queryArg) => ({ url: `/pear-reviews/${queryArg.id}` }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEAR reviews by employee ID (for managers)
      getPEARByEmployee: build.query<PEARReview[], { employeeId: string }>({
        query: (queryArg) => ({
          url: `/pear-reviews/by-employee/${queryArg.employeeId}`,
        }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEAR template (default criteria)
      getPEARTemplate: build.query<typeof import('../components/forms/models/pearReviewModel').default.initialValues, void>({
        query: () => ({ url: `/pear-reviews/template` }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEAR rating scale
      getPEARRatingScale: build.query<PEARRating[], void>({
        query: () => ({ url: `/pear-reviews/rating-scale` }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEAR summary data
      getPEARSummary: build.query<PEARSummaryItem[], { reviewPeriod?: string } | void>({
        query: (queryArg) => ({
          url: `/pear-reviews/summary`,
          params: queryArg?.reviewPeriod ? { reviewPeriod: queryArg.reviewPeriod } : {},
        }),
        providesTags: ["PEARReviewController"],
      }),

      // Get PEARs from direct reports (manager view)
      getDirectReportPEARs: build.query<PEARReview[], void>({
        query: () => ({ url: `/pear-reviews/my-direct-reports` }),
        providesTags: ["PEARReviewController"],
      }),

      // Admin: Get all PEARs
      getAdminAllPEARs: build.query<PEARReview[], { reviewPeriod?: string; status?: string } | void>({
        query: (queryArg) => ({
          url: `/pear-reviews/admin/all`,
          params: {
            ...(queryArg?.reviewPeriod ? { reviewPeriod: queryArg.reviewPeriod } : {}),
            ...(queryArg?.status ? { status: queryArg.status } : {}),
          },
        }),
        providesTags: ["PEARReviewController"],
      }),

      // Create new PEAR review
      createPEARReview: build.mutation<PEARReview, Partial<PEARReview>>({
        query: (body) => ({
          url: `/pear-reviews`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Update PEAR review
      updatePEARReview: build.mutation<void, { id: string; body: Partial<PEARReview> }>({
        query: (queryArg) => ({
          url: `/pear-reviews/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Submit PEAR review for approval
      submitPEARReview: build.mutation<PEARReview, { id: string }>({
        query: (queryArg) => ({
          url: `/pear-reviews/${queryArg.id}/submit`,
          method: "POST",
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Approve PEAR review (manager/admin)
      approvePEARReview: build.mutation<PEARReview, { id: string }>({
        query: (queryArg) => ({
          url: `/pear-reviews/${queryArg.id}/approve`,
          method: "POST",
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Manager edit PEAR review
      managerEditPEARReview: build.mutation<PEARReview, { id: string; body: Partial<PEARReview> }>({
        query: (queryArg) => ({
          url: `/pear-reviews/${queryArg.id}/manager-edit`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Delete PEAR review (admin only)
      deletePEARReview: build.mutation<void, { id: string }>({
        query: (queryArg) => ({
          url: `/pear-reviews/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["PEARReviewController"],
      }),

      // Get PEAR reviews count
      getPEARCount: build.query<{ count: number }, { where?: string } | void>({
        query: (queryArg) => ({
          url: `/pear-reviews/count`,
          params: queryArg?.where ? { where: queryArg.where } : {},
        }),
        providesTags: ["PEARReviewController"],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetMyPEARReviewsQuery,
  useGetAllPEARReviewsQuery,
  useGetPEARByIdQuery,
  useGetPEARByEmployeeQuery,
  useGetPEARTemplateQuery,
  useGetPEARRatingScaleQuery,
  useGetPEARSummaryQuery,
  useGetDirectReportPEARsQuery,
  useGetAdminAllPEARsQuery,
  useCreatePEARReviewMutation,
  useUpdatePEARReviewMutation,
  useSubmitPEARReviewMutation,
  useApprovePEARReviewMutation,
  useManagerEditPEARReviewMutation,
  useDeletePEARReviewMutation,
  useGetPEARCountQuery,
} = pearReviewApi;

export default pearReviewApi;
